import { supabase } from './supabaseClient';
import { invokeErrorMessage } from './supabaseFunctionError';

export type CrossPostPlatform = 'facebook' | 'instagram';

/** Publish or stage the same caption and photos on one social connection. */
export async function publishSocialTwin(args: {
  mode: 'now' | 'schedule';
  businessId: string;
  connectionId: string;
  platform: CrossPostPlatform;
  content: string;
  mediaIds: string[] | null;
  mediaUrls?: string[] | null;
  googleReviewIds?: string[];
  sourceLibraryMediaIds?: string[];
  scheduledFor?: string | null;
  /** feed | reel | story — mirrored so the twin posts the same way. */
  postFormat?: 'feed' | 'reel' | 'story';
  /** Public JPEG cover for a video. Omitted when the network should use the first frame. */
  thumbnailUrl?: string | null;
}): Promise<{ ok: true } | { ok: false; message: string }> {
  const mediaIds = args.mediaIds?.length ? args.mediaIds : null;
  const mediaUrls = args.mediaUrls?.length ? args.mediaUrls : null;
  const googleReviewIds = args.googleReviewIds || [];
  const sourceLibraryMediaIds = args.sourceLibraryMediaIds || [];

  if (args.platform === 'instagram' && !mediaIds?.length && !mediaUrls?.length) {
    return { ok: false, message: 'Instagram posts need a photo' };
  }

  if (args.mode === 'schedule') {
    const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
      body: {
        action: 'stage_manual',
        businessId: args.businessId,
        connectionId: args.connectionId,
        platform: args.platform,
        content: args.content,
        mediaIds,
        mediaId: mediaIds?.[0] || null,
        googleReviewIds,
        scheduledFor: args.scheduledFor,
        ...(sourceLibraryMediaIds.length ? { sourceLibraryMediaIds } : {}),
        ...(args.postFormat && args.postFormat !== 'feed' ? { postFormat: args.postFormat } : {}),
        ...(args.thumbnailUrl ? { thumbnailUrl: args.thumbnailUrl } : {}),
      },
    });
    if (error || data?.error) {
      return {
        ok: false,
        message: data?.error || (await invokeErrorMessage(error, data)) || 'Failed to stage post',
      };
    }
    return { ok: true };
  }

  const functionName =
    args.platform === 'facebook' ? 'ayrshare-facebook-posts' : 'ayrshare-instagram-posts';
  const { data, error } = await supabase.functions.invoke(functionName, {
    body: {
      action: 'create',
      businessId: args.businessId,
      connectionId: args.connectionId,
      content: args.content,
      mediaUrls,
      mediaIds,
      mediaUrl: mediaUrls?.[0] || null,
      mediaId: mediaIds?.[0] || null,
      googleReviewIds,
      ...(sourceLibraryMediaIds.length ? { sourceLibraryMediaIds } : {}),
      ...(args.postFormat && args.postFormat !== 'feed' ? { postFormat: args.postFormat } : {}),
      ...(args.thumbnailUrl ? { thumbnailUrl: args.thumbnailUrl } : {}),
    },
  });
  if (error || data?.error) {
    return {
      ok: false,
      message: data?.error || (await invokeErrorMessage(error, data)) || 'Failed to publish post',
    };
  }
  return { ok: true };
}
